import { useEffect, useState } from 'react';
import { ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '@/lib/api';

// Classify a failed /founding-filmmaker/checkout call into what the UI should do.
function classifyCheckoutError(err) {
  const msg = err.response?.data?.detail || '';
  const status = err.response?.status;
  if (status === 401) return { redirect: true };
  if (status === 409) return { toastType: 'info', text: msg || 'You already have an active Pro subscription.' };
  if (status === 410) return { toastType: 'error', text: msg || 'All 100 Founding Filmmaker spots have been claimed.' };
  return { toastType: 'error', text: msg || 'Payment service error. Please try again.' };
}

function getCtaLabel(available, loading) {
  if (!available) return 'All spots claimed';
  if (loading) return 'Opening secure checkout...';
  return <>Become a Founding Filmmaker <ArrowRight className="w-4 h-4 ml-2" strokeWidth={2} /></>;
}

/**
 * Prominent Founding Filmmaker promo panel.
 * Shows live counter, calls the isolated backend endpoint, and safely handles
 * the sold-out + already-subscribed edge cases returned by the API.
 * Anonymous visitors are sent through /signup (with `?redirect=` back to the
 * current page) so the resulting Stripe subscription is bound to a real
 * Final Frame account.
 */
export default function FoundingFilmmakerBanner({ testId = 'founding-filmmaker-banner' }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await api.get('/founding-filmmaker/status');
        if (!cancelled) setStatus(res.data);
      } catch {
        // Non-critical; hide the counter but keep the banner
        if (!cancelled) setStatus({ confirmed: 0, limit: 100, remaining: 100, available: true });
      }
    };
    load();
    // Analytics: view event (fire-and-forget; endpoint may not exist yet — safe no-op)
    try {
      api.post('/analytics/track', { event: 'founding_filmmaker.view' }).catch(() => {});
    } catch { /* ignore */ }
    return () => { cancelled = true; };
  }, []);

  const handleCTA = async () => {
    // Anonymous → send to signup, remember where to return so the
    // subscription is bound to the resulting Final Frame account.
    if (!user) {
      const back = encodeURIComponent(location.pathname + location.search);
      navigate(`/signup?redirect=${back}`);
      return;
    }
    setLoading(true);
    try {
      try { api.post('/analytics/track', { event: 'founding_filmmaker.cta_click' }).catch(() => {}); } catch { /* ignore */ }
      const res = await api.post('/founding-filmmaker/checkout', { origin_url: window.location.origin });
      const url = res.data?.checkout_url;
      if (url) {
        window.location.href = url;
      } else {
        toast.error('Could not start checkout. Please try again.');
        setLoading(false);
      }
    } catch (err) {
      const result = classifyCheckoutError(err);
      if (result.redirect) {
        const back = encodeURIComponent(location.pathname + location.search);
        navigate(`/login?redirect=${back}`);
        return;
      }
      if (result.toastType === 'info') toast.info(result.text); else toast.error(result.text);
      setLoading(false);
    }
  };

  // Number of confirmed + reserved (in-progress) checkouts is the number that
  // counts against the 100 cap. Show confirmed to the user for accuracy.
  const claimed = status?.confirmed ?? 0;
  const limit = status?.limit ?? 100;
  const available = status?.available !== false;
  const pctFilled = Math.min(100, Math.round((claimed / limit) * 100));

  return (
    <div
      className="mb-8 relative overflow-hidden rounded-xl border border-[#D6B36A]/30 bg-gradient-to-br from-[#1A1A1A] via-[#151515] to-[#1A1A1A]"
      data-testid={testId}
    >
      <div
        aria-hidden
        className="absolute -top-10 -right-10 w-52 h-52 rounded-full blur-3xl opacity-20"
        style={{ background: '#D6B36A' }}
      />
      <div className="relative p-6 sm:p-8">
        <p className="uppercase tracking-widest text-[10px] font-body font-medium text-[#D6B36A] mb-3">Limited to the first {limit} filmmakers</p>

        <h2 className="font-heading text-2xl sm:text-3xl text-[#F2F2F2] mb-1" data-testid={`${testId}-heading`}>
          Become a Founding Filmmaker
        </h2>
        <p className="text-[#D6B36A] font-heading text-sm sm:text-base tracking-wider uppercase mb-4" data-testid={`${testId}-primary`}>
          3 months of Pro for just $10
        </p>

        <div className="flex items-baseline gap-3 mb-5">
          <span className="font-heading text-5xl sm:text-6xl text-[#F2F2F2] leading-none">$10</span>
          <span className="font-body text-sm text-[#A0A0A0]">for 3 months of Pro</span>
        </div>

        <p className="text-[#A0A0A0] font-body text-sm max-w-lg mb-5 leading-relaxed">
          Be one of the first filmmakers using Final Frame and help shape what we build next.
        </p>

        <ul className="space-y-1.5 mb-6 text-[#A0A0A0] font-body text-sm" data-testid={`${testId}-benefits`}>
          <li>&middot; Full Pro access</li>
          <li>&middot; Early access to new features</li>
          <li>&middot; Help shape future Final Frame features</li>
          <li>&middot; Direct founder feedback and support</li>
          <li>&middot; Founding Filmmaker status</li>
        </ul>

        {/* Live counter */}
        <div className="mb-5" data-testid={`${testId}-counter`}>
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[#A0A0A0] font-body text-xs uppercase tracking-wider">
              <span className="text-[#F2F2F2] font-medium">{claimed}</span> / {limit} Founding Filmmakers
            </span>
            <span className="text-[#555] font-body text-[10px]">{Math.max(limit - claimed, 0)} spots left</span>
          </div>
          <div className="h-1.5 rounded-full bg-white/[0.05] overflow-hidden">
            <div
              className="h-full bg-[#D6B36A] transition-all duration-500"
              style={{ width: `${pctFilled}%` }}
              data-testid={`${testId}-progress`}
            />
          </div>
        </div>

        <Button
          onClick={handleCTA}
          disabled={loading || !available}
          className="bg-[#D6B36A] text-[#0D0D0D] hover:bg-[#E8C881] font-body font-medium h-11 px-6"
          data-testid={`${testId}-cta`}
        >
          {getCtaLabel(available, loading)}
        </Button>
        <p className="text-[#555] font-body text-xs mt-3" data-testid={`${testId}-fine-print`}>
          $10 for your first 3 months. Then $10/month. Cancel anytime.
        </p>
      </div>
    </div>
  );
}
